import { pureHttpClient } from '@/plugins/HttpManager';
import { IRequestConfig } from '@/plugins/HttpManager/interface';
import { IReoEncryptKey } from '@reolink-cloud/interface';
import { getCmkInstance } from '@/utils/cmk';
import mime from 'mime';
import { algo, enc } from 'crypto-js/core';
import WordArray from 'crypto-js/lib-typedarrays';
import Base64 from 'crypto-js/enc-base64';
import AES from 'crypto-js/aes';
import CFB from 'crypto-js/mode-cfb';
import NoPadding from 'crypto-js/pad-nopadding';
import PBKDF2 from 'crypto-js/pbkdf2';

window.URL = window.URL || window.webkitURL;
const KEY_LENGTH = 32; // AES-256 密钥长度（字节）
const IV_LENGTH = 16;

interface KeyMaterial {
    key: number[];
    iv: number[];
}

/**
 * 将CryptoJS WordArray的words数组转换为Uint8Array
 * @param words - CryptoJS的32位字数组 (大端序存储)
 * @param sigBytes - 实际有效字节数 (可选，默认使用完整数组长度)
 * @returns 原生Uint8Array格式的二进制数据
 */
function wordArrayToUint8Array( words: number[], sigBytes?: number ): Uint8Array {

    // 计算有效字节数（优先使用传入值，否则按完整数组计算）
    const totalBytes = sigBytes ?? words.length * 4;

    // 边界检查
    if ( totalBytes > words.length * 4 ) {

        throw new Error( `Invalid sigBytes: ${totalBytes} exceeds maximum ${words.length * 4}` );

    }

    const u8 = new Uint8Array( totalBytes );
    let bytePos = 0;

    for ( let wordIndex = 0; bytePos < totalBytes; wordIndex++ ) {

        const word = words[wordIndex];

        // 处理当前字的4个字节（大端序：高位字节在前）
        for ( let offset = 24; offset >= 0 && bytePos < totalBytes; offset -= 8 ) {

            u8[bytePos++] = ( word >>> offset ) & 0xff;

        }

    }

    return u8;

}

/**
 * 从加密配置中派生密钥和IV
 */
const deriveKeyFromEncrypt = async (
    encrypt: IReoEncryptKey,
    keySeed: string
): Promise<KeyMaterial> => {

    const instance = await getCmkInstance( encrypt.cmkId );

    const keyIv = PBKDF2(
        keySeed,
        Base64.parse( instance.unwrap().gen_shared_key( encrypt.devicePublicKey )),
        {
            keySize: ( KEY_LENGTH + IV_LENGTH ) / 4,
            iterations: encrypt.iterations,
            hasher: algo.SHA256
        }
    );

    // 从派生的密钥材料中提取 key 和 iv
    const key = keyIv.words.slice( 0, KEY_LENGTH / 4 );
    const iv = keyIv.words.slice( KEY_LENGTH / 4, KEY_LENGTH / 4  + IV_LENGTH / 4 );

    return { key, iv };

};

/**
 * AES-256-CFB 解密
 */
const decryptAES256CFB = (
    encryptedData: Uint8Array,
    key: number[],
    iv: number[]
): Uint8Array => {

    // 正确地将 Uint8Array 转换为 CryptoJS WordArray
    // 需要将字节数据按照32位字的格式组织
    const words: number[] = [];
    for ( let i = 0; i < encryptedData.length; i += 4 ) {

        const word =
            (( encryptedData[i] || 0 ) << 24 ) |
            (( encryptedData[i + 1] || 0 ) << 16 ) |
            (( encryptedData[i + 2] || 0 ) << 8 ) |
            ( encryptedData[i + 3] || 0 );
        words.push( word >>> 0 ); // 确保是无符号32位整数

    }

    const ciphertextWordArray = WordArray.create( words, encryptedData.length );
    const keyWordArray = WordArray.create( key );
    const ivWordArray = WordArray.create( iv );

    const decrypted = AES.decrypt(
        ciphertextWordArray.toString( enc.Base64 ),
        keyWordArray, {
            iv: ivWordArray,
            mode: CFB,
            padding: NoPadding,
        });

    return wordArrayToUint8Array( decrypted.words, decrypted.sigBytes );

};

/**
 * AES-256-GCM 解密 (使用 Web Crypto API)
 */
const decryptAES256GCM = async (
    encryptedData: Uint8Array,
    key: number[],
    iv: number[]
): Promise<Uint8Array> => {

    // 将 key 从 words 转换为 Uint8Array
    const keyBytes = wordArrayToUint8Array( key, KEY_LENGTH ); // AES-256 使用32字节密钥

    const ivBytes = wordArrayToUint8Array( iv, 12 );

    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-GCM' },
        false,
        ['decrypt']
    );

    try {

        // 对于某些GCM实现，认证标签可能已经包含在数据中
        // 先尝试直接解密整个数据
        const decryptedBuffer = await crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: ivBytes,
            },
            cryptoKey,
            encryptedData
        );

        return new Uint8Array( decryptedBuffer );

    } catch ( error ) {

        console.error( error );
        throw new Error( `AES-256-GCM decryption failed: ${error instanceof Error ? error.message : String( error )}` );

    }

};

/**
 * 执行 native-encrypt 解密
 */
const decryptNativeEncryptData = async (
    data: Uint8Array,
    encrypt: IReoEncryptKey,
    keySeed: string
): Promise<Uint8Array> => {

    const { key, iv } = await deriveKeyFromEncrypt( encrypt, keySeed );

    switch ( encrypt.algo ) {

        case 'AES-256-CFB':
            return decryptAES256CFB( data, key, iv );

        case 'AES-256-GCM':
            return decryptAES256GCM( data, key, iv );

        default:
            throw new Error( `Unsupported encryption algorithm: ${encrypt.algo}` );

    }

};

export const fetchStream = (
    url: string,
    options: IRequestConfig = {},
    encrypt?: IReoEncryptKey,
    keySeed?: string
): Promise<string> => {

    if ( !options.headers ) {

        options.headers = {};

    }

    options.responseType = 'arraybuffer';

    return new Promise(( resolve, reject ) => {

        pureHttpClient.get( url, options ).then( async res => {

            if ( res.status === 200 ) {

                let contentType = mime.getType( url.replace( /\?.*$/, '' ));

                if ( !contentType ) {

                    contentType = res.headers['content-type'];

                }

                // 处理 native-encrypt 模式
                if ( encrypt && encrypt.mode === 'native-encrypt' ) {

                    try {

                        const encryptedData = new Uint8Array( res.data );

                        if ( !keySeed ) {

                            throw new Error( 'keySeed is required for native-encrypt mode' );

                        }

                        const decryptedData = await decryptNativeEncryptData(
                            encryptedData,
                            encrypt,
                            keySeed
                        );

                        resolve( URL.createObjectURL( new Blob( [decryptedData], { type: contentType })));
                        return;

                    } catch ( error ) {

                        reject( new Error( `Native encryption decryption failed: ${error instanceof Error ? error.message : String( error )}` ));
                        return;

                    }

                }

                resolve( URL.createObjectURL( new Blob( [res.data], { type: contentType })));
                return;

            }
            reject( res.data );

        }, reject );

    });

};
